import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, filter, first, switchMap, takeUntil } from 'rxjs/operators';
import { EnvConfigLoaderService } from '../../shared/services/app/env-config-loader.service';
import { SignalRConnectionService } from '../../shared/services/signalr-connection.service';
import { ModuleRegistryService } from '../../shared/services/modules/module-registry.service';
import { EnabledModule } from '../../shared/services/modules/module-registry.models';
import { resolveModuleForUrl } from '../../shared/guards/module-route.guard';
import {
  FrameWindow,
  IframeMessagingBridgeService,
  MESSAGING_BRIDGE
} from '../../shared/services/messaging/iframe-messaging-bridge.service';

/**
 * Generic iframe host for any registry module (spec §6.2). Replaces the per-module *-ui-iframe copies.
 * Same postMessage protocol the micro-UIs already speak: REQUEST_TOKEN/AUTH_TOKEN, NAVIGATE, ROUTE_CHANGE,
 * CONTENT_HEIGHT, BREADCRUMB_OVERRIDE, TOPIC_ID, plus the messaging bridge (REQUEST_MESSAGING_STATUS,
 * OPEN_DOCUMENT_THREAD — ZEY-2907) that lets a module's record page open its thread in the shell's
 * messages panel.
 */
@Component({
  selector: 'app-host',
  templateUrl: './app-host.component.html',
  styleUrls: ['./app-host.component.scss'],
  standalone: false
})
export class AppHostComponent implements OnInit, OnDestroy {
  iframeUrl: SafeResourceUrl | null = null;
  isLoading = true;
  errorMessage: string | null = null;
  module: EnabledModule | null = null;

  private baseUrl = '';
  /** Cached once from `baseUrl` in ngOnInit — avoids re-parsing a URL on every postMessage. */
  private iframeOrigin = '';
  private destroy$ = new Subject<void>();
  private iframeElement: HTMLIFrameElement | null = null;
  private skipNextIframeUpdate = false;
  private messagingWatch?: Subscription;
  private readonly onMessage = (e: MessageEvent) => this.handleMessage(e);

  constructor(
    private sanitizer: DomSanitizer,
    private router: Router,
    private registry: ModuleRegistryService,
    private envConfigLoaderService: EnvConfigLoaderService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private signalRConnectionService: SignalRConnectionService,
    private messagingBridge: IframeMessagingBridgeService
  ) {}

  ngOnInit(): void {
    // moduleRouteGuard has already loaded the registry (and denied activation for any URL
    // with no matching module), so a synchronous read of the cached snapshot is safe here.
    this.module = resolveModuleForUrl(this.registry.modules(), this.router.url) ?? null;
    if (!this.module) {
      this.errorMessage = 'Module is not enabled for this company';
      this.isLoading = false;
      return;
    }
    this.baseUrl = this.registry.iframeBaseUrl(this.module);
    if (!this.baseUrl) {
      this.errorMessage = 'Module URL is not configured';
      this.isLoading = false;
      return;
    }
    this.iframeOrigin = new URL(this.baseUrl).origin;
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.baseUrl + this.subPath());
    this.isLoading = false;
    window.addEventListener('message', this.onMessage);
    this.messagingWatch = this.messagingBridge.watch(() => this.iframeElement?.contentWindow, this.iframeOrigin);

    this.router.events.pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$)).subscribe(() => {
      if (this.skipNextIframeUpdate) { this.skipNextIframeUpdate = false; return; }
      this.updateIframeUrl();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.messagingWatch?.unsubscribe();
    window.removeEventListener('message', this.onMessage);
  }

  onIframeLoad(event: Event): void {
    this.iframeElement = event.target as HTMLIFrameElement;
    this.sendAuthToken();
  }

  private subPath(): string {
    const prefix = this.module?.routePrefix ?? '';
    const url = this.router.url;
    return url.startsWith(prefix) ? url.substring(prefix.length) : '';
  }

  private updateIframeUrl(): void {
    const subPath = this.subPath();
    if (this.iframeElement?.contentWindow) {
      // Navigate inside the loaded SPA instead of reloading the iframe (avoids the auth flash).
      this.iframeElement.contentWindow.postMessage({ type: 'NAVIGATE', path: subPath || '/' }, this.iframeOrigin);
      return;
    }
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.baseUrl + subPath);
  }

  private sendAuthToken(): void {
    if (!this.iframeElement?.contentWindow) return;
    this.msalBroadcastService.inProgress$.pipe(
      filter((s: InteractionStatus) => s === InteractionStatus.None),
      first(),
      switchMap(() => {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length === 0) return EMPTY;
        const config = this.envConfigLoaderService.getSettings();
        const scopes = config?.interceptor?.protectedResourceMap?.[1]?.[1] || [];
        return this.msalService.acquireTokenSilent({ scopes, account: accounts[0] }).pipe(
          catchError(error => {
            if (error instanceof InteractionRequiredAuthError) {
              this.msalService.loginRedirect({ scopes });
              return EMPTY;
            }
            console.error('Failed to acquire token for module iframe:', error);
            return EMPTY;
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      const cfg = this.envConfigLoaderService.getSettings();
      this.iframeElement?.contentWindow?.postMessage({
        type: 'AUTH_TOKEN',
        accessToken: result.accessToken,
        expiresOn: result.expiresOn?.getTime(),
        apiKey: cfg?.env?.oask
      }, this.iframeOrigin);
    });
  }

  private handleMessage(event: MessageEvent): void {
    if (!this.iframeOrigin || event.origin !== this.iframeOrigin) return;
    switch (event.data?.type) {
      case 'REQUEST_TOKEN':
        this.sendAuthToken();
        break;
      case 'ROUTE_CHANGE':
        if (typeof event.data.path === 'string' && event.data.path !== this.router.url) {
          this.skipNextIframeUpdate = true;
          this.router.navigateByUrl(event.data.path, { replaceUrl: true });
        }
        break;
      case 'CONTENT_HEIGHT':
        if (typeof event.data.height === 'number' && this.iframeElement) {
          this.iframeElement.style.height = event.data.height + 'px';
        }
        break;
      case 'BREADCRUMB_OVERRIDE':
        // `replace` (optional): the URL segment to rename, e.g. a record uid (ZEY-2836).
        window.dispatchEvent(new CustomEvent('breadcrumb-override', {
          detail: { text: event.data.text || null, replace: typeof event.data.replace === 'string' ? event.data.replace : null }
        }));
        break;
      case 'TOPIC_ID':
        if (typeof event.data.topicId === 'string') this.signalRConnectionService.subscribeToTopic(event.data.topicId);
        break;
      case MESSAGING_BRIDGE.requestStatus:
      case MESSAGING_BRIDGE.openThread:
        // Discuss on a module's record page: the thread opens in the shell's messages panel (ZEY-2907).
        this.messagingBridge.handle(event.data, event.source as FrameWindow | null, event.origin);
        break;
    }
  }
}
